// src/components/AdminResetDialog.tsx
//
// iSentinel360 v0.4.6i — replaces the bare 🔑 "Reset password"
// button in NetworkView, ClientDashboard, and AdminDashboard
// with a 2-option modal:
//
//   1) Reset password — calls POST /api/users/:id/reset-password
//      (existing endpoint, v0.4.6i now also kills all sessions
//      for the user).
//
//   2) Reset device (NEW) — calls POST /api/users/:id/reset-device.
//      Sets users.apk_installed = FALSE, kills all sessions for
//      the user, and emails the user a "device reset" notification
//      in their preferred language. Used when a user gets a new
//      phone and the admin needs to allow them to install the
//      APK again.
//
// The dialog uses the existing shadcn-style Dialog primitive
// (Dialog / DialogContent / DialogHeader / DialogTitle) — same
// pattern as the rest of the dashboards.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Smartphone, X, AlertTriangle, Mail } from 'lucide-react';
import { api } from '../services/api';

export interface AdminResetDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  userEmail: string;
  /**
   * Optional custom reset-password handler. Defaults to
   * `api.post('/api/users/:id/reset-password')` (the standard
   * commerce endpoint). Civil-respondent users pass a custom
   * handler that calls the civil-org-specific endpoint
   * `/api/civil-respondents/:orgId/users/:userId/reset-password`.
   */
  resetPassword?: () => Promise<{ success: boolean; emailSent?: boolean; killedSessions?: number }>;
}

export function AdminResetDialog({
  open,
  onClose,
  userId,
  userName,
  userEmail,
  resetPassword,
}: AdminResetDialogProps) {
  const { t } = useTranslation();

  const [resetting, setResetting] = useState<'password' | 'device' | null>(null);
  const [success, setSuccess] = useState<{ kind: 'password' | 'device'; emailSent?: boolean; killedSessions?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const close = () => {
    setSuccess(null);
    setError(null);
    setResetting(null);
    onClose();
  };

  const handleResetPassword = async () => {
    setResetting('password');
    setError(null);
    try {
      // If a custom handler is provided (civil-respondent case),
      // use it. Otherwise fall back to the standard commerce
      // endpoint at /api/users/:id/reset-password.
      const r = resetPassword
        ? await resetPassword()
        : await api.post(
            `/api/users/${userId}/reset-password`,
            {},
            localStorage.getItem('i18nextLng') || undefined
          );
      setSuccess({
        kind: 'password',
        emailSent: (r as any).emailSent,
        killedSessions: (r as any).killedSessions,
      });
    } catch (e: any) {
      setError(e?.message || t('admin.passwordResetFailed') || 'Failed to reset password');
    } finally {
      setResetting(null);
    }
  };

  const handleResetDevice = async () => {
    setResetting('device');
    setError(null);
    try {
      const r = await api.resetUserDevice(userId);
      setSuccess({ kind: 'device', emailSent: r.emailSent, killedSessions: r.killedSessions });
    } catch (e: any) {
      setError(e?.message || t('admin.deviceResetFailed') || 'Failed to reset device');
    } finally {
      setResetting(null);
    }
  };

  // Success view
  if (success) {
    return (
      <div
        className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
      >
        <div className="bg-card border border-border shadow-2xl w-full max-w-md rounded-2xl p-6 relative">
          <button
            type="button"
            onClick={close}
            aria-label={t('common.close') || 'Close'}
            className="absolute top-3 right-3 p-1 rounded hover:bg-muted text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
          <h2 className="text-lg font-bold mb-3 text-foreground">
            {success.kind === 'password'
              ? t('admin.passwordResetSuccess') || 'Password reset'
              : t('admin.deviceResetSuccess') || 'Device reset'}
          </h2>
          <p className="text-sm text-muted-foreground mb-2">
            {userName} &lt;{userEmail}&gt;
          </p>
          {typeof success.killedSessions === 'number' && success.killedSessions > 0 && (
            <p className="text-xs text-muted-foreground">
              {(() => {
                const tmpl = t('admin.sessionsKilled') || '{{count}} active session(s) terminated';
                return tmpl.replace('{{count}}', String(success.killedSessions));
              })()}
            </p>
          )}
          {success.kind === 'password' && (
            <p className="text-xs text-muted-foreground mt-1">
              {success.emailSent
                ? (t('admin.passwordResetEmailSent') || 'A new password has been emailed to the user.')
                : (t('admin.passwordResetEmailFailed') || 'The email could not be sent. Please contact the user out-of-band.')}
            </p>
          )}
          {success.kind === 'device' && (
            <p className="text-xs text-muted-foreground mt-1">
              {success.emailSent
                ? (t('admin.deviceResetEmailSent') || 'The user has been emailed a device-reset notification.')
                : (t('admin.deviceResetEmailFailed') || 'The notification email could not be sent. The flag and sessions are still reset.')}
            </p>
          )}
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t('common.close') || 'Close'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation view
  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="is360-admin-reset-title"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-card border border-border shadow-2xl w-full max-w-md rounded-2xl p-6 relative my-4 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={close}
          aria-label={t('common.close') || 'Close'}
          className="absolute top-3 right-3 p-1 rounded hover:bg-muted text-muted-foreground"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 id="is360-admin-reset-title" className="text-lg font-bold mb-1 text-foreground">
          {(t('admin.resetAccessTitle') || 'Reset access for {{name}}').replace('{{name}}', userName)}
        </h2>
        <p className="text-sm text-muted-foreground mb-5">
          {userEmail}
        </p>

        {error && (
          <div className="mb-4 p-2 bg-destructive/10 text-destructive rounded text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Option 1: Reset password */}
        <div className="mb-3 rounded-lg border border-border bg-background p-4">
          <div className="flex items-start gap-3">
            <KeyRound className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground">
                {t('admin.resetPassword') || 'Reset password'}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t('admin.resetPasswordDesc') || 'Generate a new password and email it. The user must log in again on all devices.'}
              </p>
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={!!resetting}
                className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {resetting === 'password' ? (t('common.loading') || 'Loading…') : (t('admin.resetPassword') || 'Reset password')}
              </button>
            </div>
          </div>
        </div>

        {/* Option 2: Reset device (v0.4.6i — NEW) */}
        <div className="mb-5 rounded-lg border border-border bg-background p-4">
          <div className="flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground">
                {t('admin.resetDevice') || 'Reset device'}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t('admin.resetDeviceDesc') || 'Allow the user to install the app on a new phone. The password is kept. All sessions are terminated.'}
              </p>
              <button
                type="button"
                onClick={handleResetDevice}
                disabled={!!resetting}
                className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {resetting === 'device' ? (t('common.loading') || 'Loading…') : (t('admin.resetDevice') || 'Reset device')}
              </button>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground flex items-start gap-2">
          <Mail className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            {t('admin.resetEmailNote') || 'Both actions send a notification email to the user. Sessions are terminated in both cases.'}
          </span>
        </p>
      </div>
    </div>
  );
}

export default AdminResetDialog;
